import type { ClientResponse } from '../../classes/ClientResponse';

export type ClientValidationErrors = {
  rtnCode?: number;
  errCode?: string;
  errMsg?: string[];
  infoMsg?: string[];
};

export type ClientValidationErrorBag = {
  dataErrors?: string | null;
  validationErrors?: ClientValidationErrors | null;
  hasError?: boolean;
};

export type ClientValidationResponse = ClientResponse<ClientValidationErrorBag>;
export type ClientResponseLike = ClientResponse | ClientValidationErrorBag;

export type ApiSuccessResponse<TData> = {
  success: true;
  statusCode: number;
  message: string;
  data: TData;
};

export type ApiErrorResponse = {
  success: false;
  statusCode: number;
  message: string;
  errors?: string[];
};

export type ApiResponse<TData> = ApiSuccessResponse<TData> | ApiErrorResponse;

export type NullApiResponse = ApiSuccessResponse<null>;

type ErrorMessageCarrier = {
  errorMsg?: unknown;
  errMsg?: unknown;
  message?: unknown;
  response?: unknown;
  error?: unknown;
  validationErrors?: {
    errMsg?: unknown;
    infoMsg?: unknown;
  } | null;
};

export function isApiErrorResponse(value: unknown): value is ApiErrorResponse {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const record = value as Record<string, unknown>;
  return record.success === false && typeof record.message === 'string';
}

export function extractApiErrorResponse(error: unknown): ApiErrorResponse | null {
  if (isApiErrorResponse(error)) {
    return error;
  }

  if (!error || typeof error !== 'object') {
    return null;
  }

  const record = error as Record<string, unknown>;
  const nestedError = record.error;
  if (isApiErrorResponse(nestedError)) {
    return nestedError;
  }

  return null;
}

// Angular synthesizes this prefix on HttpErrorResponse.message; it is internal
// diagnostic noise (e.g. "Http failure response for http://...") and must never
// be surfaced to users.
const HTTP_FAILURE_MESSAGE_PREFIX = 'Http failure response for';

export function extractClientErrorMessages(payload: unknown): string[] {
  const seen = new Set<string>();

  const pushMessages = (value: unknown): void => {
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed && !trimmed.startsWith(HTTP_FAILURE_MESSAGE_PREFIX)) {
        seen.add(trimmed);
      }
      return;
    }

    if (Array.isArray(value)) {
      for (const item of value) {
        pushMessages(item);
      }
      return;
    }

    if (!value || typeof value !== 'object') {
      return;
    }

    const record = value as ErrorMessageCarrier;
    pushMessages(record.errorMsg);
    pushMessages(record.errMsg);
    pushMessages(record.message);
    pushMessages(record.validationErrors?.errMsg);
    pushMessages(record.response);
    pushMessages(record.error);
  };

  pushMessages(payload);
  return [...seen];
}

export function mapApiErrorToValidationBag(
  error: unknown,
  fallbackMessage: string = 'An unexpected error occurred.',
): ClientValidationErrorBag {
  const apiError = extractApiErrorResponse(error);

  let errMsg: string[];
  let message: string;

  if (apiError) {
    errMsg = apiError.errors?.filter((m): m is string => !!m?.trim()) ?? [];
    message = apiError.message?.trim() || fallbackMessage;
  } else {
    errMsg = extractClientErrorMessages(error);
    message = fallbackMessage;
  }

  return {
    dataErrors: errMsg.length === 0 ? message : null,
    validationErrors:
      errMsg.length > 0
        ? {
            errMsg,
            infoMsg: message && message !== fallbackMessage ? [message] : [],
          }
        : null,
    hasError: true,
  };
}
